import type { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { queryOptions } from "@tanstack/react-query";
import type {
  AppointmentPreviousSoapNoteSelectResponse,
  AppointmentPreviousSoapNotesResponse,
} from "../../../shared/api-types";
import { NetworkRequestError, authenticatedApi } from "./api";

export type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

export class PreviousSoapNotesScopeChangedError extends Error {
  constructor() {
    super("Previous SOAP Note scope changed");
    this.name = "PreviousSoapNotesScopeChangedError";
  }
}

export const PREVIOUS_SOAP_NOTES_CACHE_MS = 60 * 60_000;
const PREVIOUS_SOAP_NOTES_QUERY = "appointment-previous-soap-notes";
const PREVIOUS_SOAP_NOTE_QUERY = "appointment-previous-soap-note";
type PreviousSoapNotesClientOperation =
  | "browse_recent_signed_notes"
  | "fetch_recent_signed_note";

export interface PreviousSoapNotesTelemetry {
  networkFailure(operation: PreviousSoapNotesClientOperation): void;
}

const sentryTelemetry: PreviousSoapNotesTelemetry = {
  networkFailure(operation) {
    const message =
      operation === "browse_recent_signed_notes"
        ? "appointment_previous_soap_notes_list_network_failed"
        : "appointment_previous_soap_note_selection_network_failed";
    Sentry.captureException(new Error(message), {
      tags: {
        code: "network_error",
        operation,
        source: "client",
        surface: "appointments",
      },
    });
  },
};

export function previousSoapNotesQueryKey(
  appointmentId: string,
  previousSoapNotesScope: string,
  rdId: string | null = null
) {
  return [
    PREVIOUS_SOAP_NOTES_QUERY,
    appointmentId,
    previousSoapNotesScope,
    rdId,
  ] as const;
}

export function previousSoapNoteQueryKey(
  appointmentId: string,
  previousSoapNotesScope: string,
  noteId: string,
  rdId: string | null = null
) {
  return [
    PREVIOUS_SOAP_NOTE_QUERY,
    appointmentId,
    previousSoapNotesScope,
    noteId,
    rdId,
  ] as const;
}

export function previousSoapNotesQueryOptions(
  getToken: ClerkGetToken,
  appointmentId: string,
  previousSoapNotesScope: string,
  telemetry: PreviousSoapNotesTelemetry = sentryTelemetry,
  rdId: string | null = null
) {
  return queryOptions({
    queryKey: previousSoapNotesQueryKey(appointmentId, previousSoapNotesScope, rdId),
    queryFn: () =>
      captureNetworkFailure("browse_recent_signed_notes", telemetry, async () => {
        const response = await authenticatedApi<{
          data: AppointmentPreviousSoapNotesResponse;
        }>(getToken, previousSoapNotesPath(appointmentId, rdId), { cache: "no-store" });
        requireScope(response.data.previousSoapNotesScope, previousSoapNotesScope);

        return response.data;
      }),
    staleTime: PREVIOUS_SOAP_NOTES_CACHE_MS,
    gcTime: PREVIOUS_SOAP_NOTES_CACHE_MS,
  });
}

export function previousSoapNoteQueryOptions(
  getToken: ClerkGetToken,
  appointmentId: string,
  previousSoapNotesScope: string,
  noteId: string,
  telemetry: PreviousSoapNotesTelemetry = sentryTelemetry,
  rdId: string | null = null
) {
  return queryOptions({
    queryKey: previousSoapNoteQueryKey(
      appointmentId,
      previousSoapNotesScope,
      noteId,
      rdId
    ),
    queryFn: () =>
      captureNetworkFailure("fetch_recent_signed_note", telemetry, async () => {
        const response = await authenticatedApi<{
          data: AppointmentPreviousSoapNoteSelectResponse;
        }>(getToken, previousSoapNotesPath(appointmentId, rdId, true), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ noteId }),
        });
        requireScope(response.data.previousSoapNotesScope, previousSoapNotesScope);

        return response.data.note;
      }),
    staleTime: PREVIOUS_SOAP_NOTES_CACHE_MS,
    gcTime: PREVIOUS_SOAP_NOTES_CACHE_MS,
  });
}

function previousSoapNotesPath(
  appointmentId: string,
  rdId: string | null,
  select = false
): string {
  const suffix = select ? "/select" : "";
  const path = `/api/appointments/${encodeURIComponent(appointmentId)}/previous-soap-notes${suffix}`;

  return rdId ? `${path}?rdId=${encodeURIComponent(rdId)}` : path;
}

async function captureNetworkFailure<T>(
  operation: PreviousSoapNotesClientOperation,
  telemetry: PreviousSoapNotesTelemetry,
  request: () => Promise<T>
): Promise<T> {
  try {
    return await request();
  } catch (error) {
    if (error instanceof NetworkRequestError) {
      telemetry.networkFailure(operation);
    }

    throw error;
  }
}

function requireScope(actualScope: string, expectedScope: string): void {
  if (actualScope !== expectedScope) {
    throw new PreviousSoapNotesScopeChangedError();
  }
}
